// src/contexts/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import { authService } from '../services/auth';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // --- FETCH USER PROFILE ---
  const refreshProfile = async () => {
    try {
      const userData = await authService.getProfile();
      setUser(userData);
      localStorage.setItem('currentUser', JSON.stringify(userData));
    } catch (error) {
      console.error("Failed to refresh profile:", error);
      // If the session is revoked/expired, force logout
      if (error.message === 'SESSION_REVOKED') {
        logout(true); 
      }
    }
  };

  // --- INITIALIZATION ---
  useEffect(() => {
    const initAuth = async () => {
      const savedUser = localStorage.getItem('currentUser');
      const token = localStorage.getItem('token');

      if (savedUser && token) {
        // 1. Load cached user immediately for speed
        setUser(JSON.parse(savedUser));
        
        // 2. Verify session validity & get fresh data in background
        await refreshProfile();
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  // --- LOGIN ---
  // Note: The actual UI flow (credentials -> 2FA) happens in Login.js.
  const loginSuccess = (userData) => {
    setUser(userData);
    localStorage.setItem('currentUser', JSON.stringify(userData));
  };

  // --- LOGOUT ---
  const logout = async (isRevoked = false) => {
    const token = localStorage.getItem('token');

    if (token && !isRevoked) {
      try {
        // 1. Wait for the network request to finish
        await authService.logout(); 
      } catch (err) {
        console.error("Logout failed");
      }
    }

    // 2. ONLY NOW clear storage and redirect
    setUser(null);
    localStorage.removeItem('currentUser');
    localStorage.removeItem('token');
    localStorage.removeItem('sessionId');
    localStorage.removeItem('userRole');
    
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  };



  const value = {
    user,
    loginSuccess, // Used by Login.js to update state
    logout,
    isLoading,
    refreshProfile 
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
